import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { List, ListItem } from '@mui/material';
import Popover from '@mui/material/Popover';
import CheckIcon from '@mui/icons-material/Check';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

const buildStyles = (appColors) => ({
  buttonGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  dateButton: {
    backgroundColor: appColors.evergreen,
    color: 'white',
    textTransform: 'none',
    fontWeight: 600,
    height: '40px',
    borderRadius: '8px',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: appColors.leaf,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
      marginLeft: '8px',
    }
  },
  clearButton: {
    backgroundColor: '#f8fafc',
    color: appColors.evergreen,
    border: `1px solid ${appColors.evergreen}`,
    textTransform: 'none',
    fontWeight: 600,
    height: '40px',
    borderRadius: '8px',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: '#f1f5f9',
      borderColor: appColors.leaf,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
      marginRight: '4px',
    }
  },
  popoverCard: {
    minWidth: { xs: '200px', sm: '220px' },
    boxShadow: '0 4px 6px -1px rgba(31, 122, 84, 0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(31, 122, 84, 0.1)'
  },
  listButton: {
    width: '100%',
    justifyContent: 'left',
    padding: '8px 16px',
    color: '#1e293b',
    '&:hover': {
      backgroundColor: 'rgba(31, 122, 84, 0.04)',
    },
    textTransform: 'none',
    fontWeight: 500
  }
});

function DateFilter(props, ref) {
  const { onApply,Onreset } = props;
  const { colors: appColors } = useAppTheme();
  const useStyles = buildStyles(appColors);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [buttonText, setButtonText] = useState('Date Range');
  const [selectedValue, setSelectedValue] = useState('');
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  
  const modifyDateText = [
    { label: 'Today', value: 'today' },
    { label: 'Last 7 Days', value: '7days' },
    { label: 'Last 15 Days', value: '15days' },
    { label: 'Last 30 Days', value: '30days' },
  ];

  const handleListItemClick = (item, value) => {
    setButtonText(item);
    setSelectedValue(value);
    onApply(value);
    setAnchorEl(null);
  };

  const handleResetClick = () => {
    setSelectedValue(null);
    setButtonText('Date Range');
    Onreset(''); // Call API with empty value to reset
  };

  useEffect(() => {
    if (ref) {
      ref.current = {
        handleResetClick,
      };
    }
  }, [ref]);

  return (
    <Box sx={useStyles.buttonGroup}>
      <Button
        onClick={handleClick}
        sx={useStyles.dateButton}
        startIcon={buttonText === 'Date Range' ? <CalendarMonthIcon /> : <CheckIcon />}
      >
        {buttonText}
      </Button>

      <Button
        onClick={handleResetClick}
        sx={useStyles.clearButton}
        startIcon={<FilterAltOffIcon />}
      >
        Clear
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: useStyles.popoverCard
        }}
      >
        <Box>
          <List sx={{ p: 0 }}>
            {modifyDateText.map((item) => (
              <ListItem key={item.value} sx={{ p: 0 }}>
                <Button
                  sx={useStyles.listButton}
                  onClick={() => handleListItemClick(item.label, item.value)}
                >
                  {item.label}
                </Button>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
}

export default React.forwardRef(DateFilter);
